/**
 * CreateExperimentStep1
 *
 * Figma implementation of the Create Experiment dialog body.
 * Design source: Figma  (Body)
 *
 *
 */

import React from 'react';

import { UploadProgressAndStatus } from './UploadProgressAndStatus';
import dropdownIcon from '../assets/drop-down-chevron.svg';
import { FileUpload } from '../common/FileUpload';
import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from '../constants/constants';
import { Organism } from '../types/experiment';

export interface CreateExperimentStep1Props {
  experimentId: string;
  title: string;
  onTitleChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  organism: string;
  onOrganismChange: (value: string) => void;
  titleError?: string;
  descriptionError?: string;
  candidateInformation: string;
  onCandidateInformationChange: (fileLocation: string) => void;
  onFileDrop?: () => Promise<void>;
}

export function CreateExperimentStep1({
  experimentId,
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  organism,
  onOrganismChange,
  titleError,
  descriptionError,
  candidateInformation,
  onCandidateInformationChange,
  onFileDrop,
}: CreateExperimentStep1Props) {
  return (
    <div className="flex flex-col items-start gap-2 px-[90px] w-full flex-1">
      {/* Title Field */}
      <div className="flex flex-col items-start w-full">
        {/* Label Row */}
        <div className="flex flex-row gap-[3px] items-start">
          <span className="text-form-label">Title</span>
          <span className="text-required-asterisk">*</span>
        </div>

        {/* Input */}
        <div className="form-input">
          <input type="text" placeholder="Type here..." value={title} onChange={(e) => onTitleChange(e.target.value)} maxLength={TITLE_MAX_LENGTH + 1} />
        </div>

        {/* Character count + Error */}
        <div className="flex justify-between w-full">
          {titleError || title?.length > TITLE_MAX_LENGTH ? (
            <span className="text-xs text-error-red mt-1">{titleError || `Title must be ${TITLE_MAX_LENGTH} characters or fewer`}</span>
          ) : (
            <span />
          )}
          <span className={`text-xs mt-1 ${title?.length > TITLE_MAX_LENGTH ? 'text-error-red' : 'text-form-border'}`}>
            {title?.length}/{TITLE_MAX_LENGTH}
          </span>
        </div>
      </div>

      {/* Description Field */}
      <div className="flex flex-col items-start w-full">
        {/* Label Row */}
        <div className="flex flex-row gap-[3px] items-start">
          <div className="flex items-start pb-1">
            <span className="text-form-label">Description</span>
          </div>
        </div>

        {/* Textarea */}
        <div className="form-textarea">
          <textarea
            placeholder="Optional notes or experiment details..."
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            maxLength={DESCRIPTION_MAX_LENGTH + 1}
          />
        </div>

        {/* Character count + Error */}
        <div className="flex justify-between w-full">
          {descriptionError || description?.length > DESCRIPTION_MAX_LENGTH ? (
            <span className="text-xs text-error-red mt-1">{descriptionError || `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`}</span>
          ) : (
            <span />
          )}
          <span className={`text-xs mt-1 ${description?.length > DESCRIPTION_MAX_LENGTH ? 'text-error-red' : 'text-form-border'}`}>
            {description?.length}/{DESCRIPTION_MAX_LENGTH}
          </span>
        </div>
      </div>

      {/* Organism Field */}
      <div className="flex flex-col items-start w-full">
        <div className="flex flex-row gap-[3px] items-start">
          <span className="text-form-label">Organism</span>
          <span className="text-required-asterisk">*</span>
        </div>

        <div className="relative w-full">
          <select className="form-select" value={organism} onChange={(e) => onOrganismChange(e.target.value)}>
            <option value={Organism.MOUSE}>Mouse</option>
            <option value={Organism.HUMAN}>Human</option>
          </select>
          <div className="absolute right-[10px] top-1/2 -translate-y-1/2 pointer-events-none">
            <img src={dropdownIcon} alt="" className="w-[10px] h-[6px]" />
          </div>
        </div>
      </div>

      {/* Antibody Information Field */}
      <div className="w-full pt-2">
        <FileUpload
          experimentId={experimentId}
          label="Antibody Information"
          description=""
          subtitle="Click here or drag-and-drop to upload"
          required
          fileTypes={['csv']}
          fileName={candidateInformation}
          onFileChange={onCandidateInformationChange}
          onFileDrop={onFileDrop}
          helpText="Accepting csv exports from 10x genomics CellRanger"
        />
        <div className="w-full mt-2">
          <UploadProgressAndStatus experimentId={experimentId} />
        </div>
      </div>
    </div>
  );
}

export default CreateExperimentStep1;
